import './Hero.scss';

import { type MouseEvent } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { faArrowDown, faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@myome/ui';

import { CasrcadeLockup } from '@components/marketing/CasrcadeLockup/CasrcadeLockup';

import { useFvtWelcomeCta } from '../useFvtWelcomeCta';

interface HeroProps {
  heroRef: React.Ref<HTMLElement>;
  learnMoreTargetId: string;
}

export const Hero = ({ heroRef, learnMoreTargetId }: HeroProps) => {
  const { onInviteCta } = useFvtWelcomeCta();

  const onLearnMoreClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(learnMoreTargetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section ref={heroRef} className='fvt-hero pt-5 pb-5' aria-labelledby='fvt-hero-heading'>
      <Container fluid='xxl'>
        <CasrcadeLockup />

        <Row className='g-5 align-items-start'>
          <Col md={7} className='fvt-hero__primary'>
            <div className='fvt-hero__eyebrow mb-3'>Family Cascade Genetic Testing</div>

            <h1 id='fvt-hero-heading' className='fvt-hero__heading mb-3'>
              Your provider sent you here to take the next step.
            </h1>

            <p className='fvt-hero__lead mb-4'>
              Your provider has shared a copy of your hypoparathyroidism genetic testing report with us. We can offer
              no-charge genetic testing for your closest blood relatives. We just need a few minutes from you to get
              them started.
            </p>

            <a
              href={`#${learnMoreTargetId}`}
              className='fvt-hero__learn-more d-inline-flex gap-2 align-items-center'
              onClick={onLearnMoreClick}
            >
              Learn more
              <span className='fvt-hero__learn-arrow' aria-hidden='true'>
                <FontAwesomeIcon icon={faArrowDown} />
              </span>
            </a>
          </Col>

          <Col md={5} className='fvt-hero__aside'>
            <div className='fvt-getstarted-card px-4 py-3'>
              <h2 className='fvt-getstarted-card__title mb-2'>Get started</h2>

              <p className='fvt-getstarted-card__body mb-3'>
                Add your blood relatives (up to 4th degree), and we&apos;ll send each a private invitation.
              </p>

              <Button
                className='w-100'
                type='button'
                variant='primary'
                startIcon={<FontAwesomeIcon icon={faUserPlus} />}
                onClick={onInviteCta}
              >
                Invite My Relatives
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};
